/**
 * @fileoverview Atom: a labelled dropdown for a closed set of choices.
 * @module components/atoms/Select
 */

import { useId, type ReactNode, type SelectHTMLAttributes } from "react"

import { Icon } from "../Icon/Icon"

import "./Select.css"

/** Props accepted by {@link Select}. */
export interface SelectProps extends Omit<
	SelectHTMLAttributes<HTMLSelectElement>,
	"className" | "id"
> {
	/** Visible label. Always present: a placeholder is not a label. */
	readonly label: string
	/** Supporting text shown under the control. */
	readonly hint?: string
	/** Validation message. Its presence marks the field invalid. */
	readonly error?: string
	/** The `<option>` (and optionally `<optgroup>`) elements. */
	readonly children: ReactNode
}

/**
 * Labelled dropdown, styled to match {@link Input}.
 *
 * @param props - Label, optional hint and error, plus native select props.
 * @returns The rendered field, label and messages wired for accessibility.
 *
 * @example
 * <Select label="Catégorie" value={category} onChange={handleChange}>
 *   <option value="">Sans catégorie</option>
 *   <option value="Sport">Sport</option>
 * </Select>
 */
export function Select({ label, hint, error, children, ...rest }: SelectProps) {
	const id = useId()
	const hintId = `${id}-hint`
	const errorId = `${id}-error`

	const describedBy =
		[
			hint === undefined ? null : hintId,
			error === undefined ? null : errorId,
		]
			.filter((value): value is string => value !== null)
			.join(" ") || undefined

	return (
		<div className="select-field">
			<label className="select-field__label" htmlFor={id}>
				{label}
			</label>
			<div className="select-field__wrap">
				<select
					id={id}
					className="select-field__control"
					aria-invalid={error === undefined ? undefined : true}
					{...(describedBy === undefined
						? {}
						: { "aria-describedby": describedBy })}
					{...rest}
				>
					{children}
				</select>
				<Icon name="chevron-down" size={16} />
			</div>
			{hint !== undefined && (
				<span className="select-field__hint" id={hintId}>
					{hint}
				</span>
			)}
			{error !== undefined && (
				<span className="select-field__error" id={errorId} role="alert">
					{error}
				</span>
			)}
		</div>
	)
}
